import { Link } from 'react-router-dom';
import type { NewsArticle } from '@/types/news';
import { formatDate, formatReadCount, getCategoryName } from '@/utils/news';

interface NewsCardProps {
  article: NewsArticle;
  variant?: 'card' | 'row';
}

export default function NewsCard({ article, variant = 'card' }: NewsCardProps) {
  const to = `/detail/${article.id}`;
  const categoryName = getCategoryName(article.category);

  if (variant === 'row') {
    return (
      <Link
        to={to}
        className="group flex gap-4 p-3 md:p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 hover:bg-primary-50/40 transition-colors cursor-pointer"
      >
        <div className="w-28 h-20 sm:w-40 sm:h-28 shrink-0 rounded-md overflow-hidden bg-background-100">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} - 9·1牛网`}
            className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <h3 className="font-heading font-bold text-sm md:text-base text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="hidden sm:block mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
            {article.summary}
          </p>
          <div className="mt-auto pt-2 flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <i className="ri-price-tag-3-line" />
              {categoryName}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line" />
              {formatDate(article.publishTime)}
            </span>
            <span className="hidden sm:inline-flex items-center gap-1">
              <i className="ri-fire-line" />
              {formatReadCount(article.readCount)} 阅读
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="w-full h-44 md:h-48 overflow-hidden bg-background-100">
        <img
          src={article.cover}
          alt={`${article.title} 配图`}
          title={`${article.title} - 9·1牛网`}
          className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">
            {categoryName}
          </span>
          {article.tags.slice(0, 1).map((tag) => (
            <span key={tag} className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-800">
              {tag}
            </span>
          ))}
        </div>
        <h3 className="font-heading font-bold text-base text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.summary}</p>
        <div className="mt-auto pt-3 flex items-center justify-between text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-time-line" />
            {formatDate(article.publishTime)}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-fire-line" />
            {formatReadCount(article.readCount)}
          </span>
        </div>
      </div>
    </Link>
  );
}